"use client";

/**
 * `<HeroStats>` — the hero stat row (9 / 7 / Weekly) with count-ups.
 *
 * Client island inside the server-rendered landing hero: the two numeric
 * stats ease 0 → target via `useCountUp` (600ms, the motion-system
 * count-up duration); "Weekly" is static text. Under
 * prefers-reduced-motion the hook renders the final numbers instantly.
 *
 * The descriptive labels are plain text and therefore present in the
 * SSR HTML (client components still server-render their initial markup).
 */

import { useCountUp } from "@/lib/motion";

interface StatDef {
  /** Numeric stats count up; string stats render statically. */
  value: number | string;
  label: string;
}

const STATS: StatDef[] = [
  { value: 9, label: "Analysis phases per report" },
  { value: 7, label: "Specialized analyzers" },
  { value: "Weekly", label: "Research cycles, every Sunday" },
];

function StatValue({ value }: { value: number | string }) {
  // Hooks can't be conditional — run the count-up unconditionally and
  // only use it when the stat is numeric.
  const counted = useCountUp(typeof value === "number" ? value : 0);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {typeof value === "number" ? Math.round(counted) : value}
    </span>
  );
}

export function HeroStats() {
  return (
    <div
      style={{
        marginTop: 56,
        paddingTop: 28,
        borderTop: "1px solid var(--color-line-2)",
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 24,
        maxWidth: 560,
      }}
    >
      {STATS.map((s) => (
        <div key={s.label}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 600,
              color: "var(--color-text)",
              letterSpacing: "-0.02em",
            }}
          >
            <StatValue value={s.value} />
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-dim)",
              marginTop: 4,
              letterSpacing: "0.02em",
              lineHeight: 1.4,
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}
